import type { PracticeBand } from "./practiceBands";
import type { PracticeGeneratorMeta } from "./practiceGeneratorCatalog";
import type { PracticeDraw } from "./practiceGenerators";
import type { PracticeLocale } from "./practiceLocale";
import type { PracticeDifficulty } from "./practiceQuestionTemplates";
import type { PracticeSubject } from "./practiceSubjects";

type Sampler = (rng: () => number, locale: PracticeLocale) => PracticeDraw;

function randInt(rng: () => number, min: number, max: number): number {
  const lo = Math.ceil(min);
  const hi = Math.floor(max);
  return lo + Math.floor(rng() * (hi - lo + 1));
}

function choice<T>(rng: () => number, items: readonly T[]): T {
  return items[randInt(rng, 0, items.length - 1)];
}

function text(locale: PracticeLocale, en: string, he: string): string {
  return locale === "he" ? he : en;
}

function lines(
  locale: PracticeLocale,
  pairs: ReadonlyArray<readonly [string, string]>,
): string[] {
  return pairs.map(([en, he]) => text(locale, en, he));
}

function repeatMultiply(start: number, factor: number, times: number): number {
  let value = start;
  for (let i = 0; i < times; i += 1) value *= factor;
  return value;
}

function formatLinearFn(a: number, b: number): string {
  const head = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
  if (b === 0) return head;
  return b > 0 ? `${head} + ${b}` : `${head} - ${Math.abs(b)}`;
}

function formatQuadraticFn(a: number, b: number, c: number): string {
  const head = a === 1 ? "x^2" : `${a}x^2`;
  let out = head;
  if (b !== 0) {
    const term = Math.abs(b) === 1 ? "x" : `${Math.abs(b)}x`;
    out += b > 0 ? ` + ${term}` : ` - ${term}`;
  }
  if (c !== 0) out += c > 0 ? ` + ${c}` : ` - ${Math.abs(c)}`;
  return out;
}

function monomial(coeff: number, exp: number): string {
  if (exp <= 0) return String(coeff);
  const body = exp === 1 ? "x" : `x^${exp}`;
  if (coeff === 1) return body;
  if (coeff === -1) return `-${body}`;
  return `${coeff}${body}`;
}

function compactLinear(a: number, b: number): string {
  const head = a === 1 ? "x" : `${a}x`;
  if (b === 0) return head;
  return b > 0 ? `${head}+${b}` : `${head}-${Math.abs(b)}`;
}

function slopeInterceptEquation(m: number, b: number): string {
  const head = m === 1 ? "x" : m === -1 ? "-x" : `${m}x`;
  if (b === 0) return `y=${head}`;
  return b > 0 ? `y=${head}+${b}` : `y=${head}-${Math.abs(b)}`;
}

function radicalAnswer(
  coeff: number,
  radicand: number,
): { expectedAnswer: string; acceptedAnswers: string[] } {
  if (coeff === 1) {
    return {
      expectedAnswer: `sqrt(${radicand})`,
      acceptedAnswers: [`√${radicand}`],
    };
  }
  return {
    expectedAnswer: `${coeff}sqrt(${radicand})`,
    acceptedAnswers: [`${coeff}√${radicand}`, `${coeff}*sqrt(${radicand})`],
  };
}

function powerExpr(base: number, exp: number): string {
  return base < 0 ? `(${base})^${exp}` : `${base}^${exp}`;
}

function formatRoot(value: number): string {
  if (Number.isInteger(value)) return String(value);
  const scaled = Math.round(value * 2);
  if (Math.abs(value * 2 - scaled) < 1e-9) {
    const sign = scaled < 0 ? "-" : "";
    return `${sign}${Math.abs(scaled)}/2`;
  }
  return String(value);
}

function formatExcluded(root: number): string {
  return root === 0 ? "x≠0" : root > 0 ? `x≠-${root}` : `x≠${Math.abs(root)}`;
}

function excludedZeroAt(root: number): string {
  return root === 0 ? "x = 0" : root > 0 ? `x = -${root}` : `x = ${Math.abs(root)}`;
}

function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x || 1;
}

function constTerm(n: number): string {
  if (n === 0) return "";
  return n > 0 ? ` + ${n}` : ` - ${Math.abs(n)}`;
}

function linearExpr(a: number, b: number): string {
  const head = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
  return `${head}${constTerm(b)}`;
}

function poly(coeffs: number[]): string {
  let out = "";
  for (let i = 0; i < coeffs.length; i += 1) {
    const power = coeffs.length - 1 - i;
    const coeff = coeffs[i];
    if (coeff === 0) continue;
    const rest = power === 0 ? "" : power === 1 ? "x" : `x^${power}`;
    const mag = Math.abs(coeff);
    const body = rest && mag === 1 ? rest : `${mag}${rest}`;
    const sign = coeff < 0 ? "-" : out ? "+" : "";
    out += `${sign}${body}`;
  }
  return out || "0";
}

function parenFactor(root: number): string {
  if (root === 0) return "x";
  return root > 0 ? `(x+${root})` : `(x-${Math.abs(root)})`;
}

function row(
  id: string,
  gradeMin: number,
  gradeMax: number,
  difficulty: PracticeDifficulty,
  subject: PracticeSubject,
  band: PracticeBand,
  en: string,
  he: string,
): PracticeGeneratorMeta {
  return {
    id,
    skillId: id,
    gradeMin,
    gradeMax,
    difficulty,
    subject,
    band,
    name: { en, he },
    skillLabel: { en, he },
  };
}

export const HIGH_SCHOOL_GENERATOR_CATALOG: readonly PracticeGeneratorMeta[] = [
  row("algebra.linear-one-step", 7, 8, "intro", "Algebra", "middle", "Solve a linear equation", "פתרון משוואה לינארית"),
  row("algebra.expand-binomial", 7, 8, "intro", "Algebra", "middle", "Expand a binomial", "פתיחת בינום"),
  row("algebra.slope", 8, 9, "core", "Algebra", "middle", "Slope between two points", "שיפוע בין שתי נקודות"),
  row("algebra.system", 8, 9, "core", "Algebra", "middle", "Solve a linear system", "פתרון מערכת משוואות"),
  row("algebra.linear-inequality", 8, 9, "core", "Algebra", "middle", "Solve a linear inequality", "פתרון אי-שוויון לינארי"),
  row("algebra.factor-quadratic", 10, 11, "core", "Algebra", "high", "Factor a quadratic", "פירוק טרינום"),
  row("algebra.vertex-form", 11, 12, "challenge", "Algebra", "high", "Vertex of a parabola", "קודקוד של פרבולה"),
  row("trig.exact-30-60", 10, 11, "intro", "Trigonometry", "high", "Sine and cosine of 30° and 60°", "סינוס וקוסינוס של 30° ו-60°"),
  row("trig.exact-axis", 10, 11, "intro", "Trigonometry", "high", "Sine and cosine on the axes", "סינוס וקוסינוס על הצירים"),
  row("trig.tan-special", 10, 11, "intro", "Trigonometry", "high", "Tangent of a special angle", "טנגנס של זווית מיוחדת"),
  row("trig.right-ratio", 10, 11, "core", "Trigonometry", "high", "Right-triangle ratio", "יחס במשולש ישר-זווית"),
  row("trig.inverse-special", 10, 11, "core", "Trigonometry", "high", "Inverse sine or cosine", "סינוס או קוסינוס הפוך"),
  row("calc.power-rule", 11, 12, "intro", "Calculus", "high", "Power rule", "כלל החזקה"),
  row("calc.product-rule", 11, 12, "core", "Calculus", "high", "Product rule", "כלל המכפלה"),
  row("calc.chain-rule", 11, 12, "challenge", "Calculus", "high", "Chain rule", "כלל השרשרת"),
  row("calc.indefinite-integral", 11, 12, "core", "Calculus", "high", "Indefinite integral", "אינטגרל לא מסוים"),
  row("calc.definite-integral", 11, 12, "core", "Calculus", "high", "Definite integral", "אינטגרל מסוים"),
  row("vectors.add", 11, 12, "intro", "Vectors", "high", "Add vectors", "חיבור וקטורים"),
  row("vectors.magnitude", 11, 12, "intro", "Vectors", "high", "Vector magnitude", "אורך של וקטור"),
  row("vectors.scalar", 11, 12, "intro", "Vectors", "high", "Scalar multiple", "כפל בסקלר"),
  row("vectors.dot", 11, 12, "core", "Vectors", "high", "Dot product", "מכפלה סקלרית"),
  row("complex.powers-of-i", 11, 12, "intro", "Complex", "high", "Powers of i", "חזקות של i"),
  row("complex.add", 11, 12, "intro", "Complex", "high", "Add complex numbers", "חיבור מספרים מרוכבים"),
  row("complex.modulus", 11, 12, "core", "Complex", "high", "Modulus", "מודול"),
  row("algebra.exponent-product", 10, 11, "intro", "Algebra", "high", "Product of powers", "מכפלת חזקות"),
  row("algebra.exponent-power", 10, 11, "core", "Algebra", "high", "Power of a power", "חזקה של חזקה"),
  row("algebra.zero-negative-exponent", 10, 11, "core", "Algebra", "high", "Zero and negative exponents", "מעריך אפס ומעריך שלילי"),
  row("algebra.function-value", 10, 11, "intro", "Algebra", "high", "Evaluate a function", "ערך של פונקציה"),
  row("algebra.solve-quadratic", 10, 11, "core", "Algebra", "high", "Solve a quadratic equation", "פתרון משוואה ריבועית"),
  row("algebra.logarithm", 11, 12, "core", "Algebra", "high", "Evaluate a logarithm", "חישוב לוגריתם"),
  row("sequences.arithmetic-term", 10, 11, "intro", "Algebra", "high", "Arithmetic sequence term", "איבר בסדרה חשבונית"),
  row("sequences.geometric-term", 10, 11, "core", "Algebra", "high", "Geometric sequence term", "איבר בסדרה הנדסית"),
  row("trig.special-right", 10, 11, "core", "Trigonometry", "high", "Special right triangle", "משולש ישר-זווית מיוחד"),
  row("complex.multiply", 11, 12, "core", "Complex", "high", "Multiply complex numbers", "כפל מספרים מרוכבים"),
  row("algebra.exponent-quotient", 10, 11, "core", "Algebra", "high", "Quotient of powers", "מנת חזקות"),
  row("algebra.scientific-notation", 10, 11, "intro", "Algebra", "high", "Scientific notation", "כתיב מדעי"),
  row("algebra.absolute-value", 9, 9, "core", "Algebra", "middle", "Absolute value equation", "משוואת ערך מוחלט"),
  row("algebra.evaluate-linear", 9, 9, "intro", "Algebra", "middle", "Evaluate a linear function", "ערך של פונקציה קווית"),
  row("algebra.slope-intercept-line", 9, 9, "core", "Algebra", "middle", "Slope-intercept equation", "משוואת ישר לפי שיפוע"),
  row("algebra.quadratic-formula", 10, 11, "challenge", "Algebra", "high", "Quadratic formula", "נוסחת השורשים"),
  row("algebra.rational-simplify", 10, 11, "core", "Algebra", "high", "Simplify a rational expression", "צמצום ביטוי רציונלי"),
  row("algebra.rational-exponent", 10, 11, "core", "Algebra", "high", "Rational exponent", "מעריך רציונלי"),
  row("algebra.exponential-value", 11, 12, "intro", "Algebra", "high", "Evaluate an exponential", "חישוב חזקה"),
];

const EXACT_30_60 = [
  { fn: "sin", deg: 30, expected: "1/2", accepted: [] as string[] },
  { fn: "cos", deg: 60, expected: "1/2", accepted: [] as string[] },
  { fn: "sin", deg: 60, expected: "sqrt(3)/2", accepted: ["√3/2", "(sqrt(3))/2"] },
  { fn: "cos", deg: 30, expected: "sqrt(3)/2", accepted: ["√3/2", "(sqrt(3))/2"] },
] as const;

const AXIS = [
  { fn: "sin", deg: 0, expected: "0" },
  { fn: "cos", deg: 0, expected: "1" },
  { fn: "sin", deg: 90, expected: "1" },
  { fn: "cos", deg: 90, expected: "0" },
  { fn: "sin", deg: 180, expected: "0" },
  { fn: "cos", deg: 180, expected: "-1" },
  { fn: "sin", deg: 270, expected: "-1" },
  { fn: "cos", deg: 270, expected: "0" },
] as const;

const TRIPLES = [
  [3, 4, 5],
  [5, 12, 13],
  [6, 8, 10],
  [8, 15, 17],
  [7, 24, 25],
] as const;

function complexTerm(real: number, imag: number): string {
  if (imag === 0) return String(real);
  const imagBody = imag === 1 ? "i" : imag === -1 ? "-i" : `${imag}i`;
  if (real === 0) return imagBody;
  if (imagBody.startsWith("-")) return `${real}${imagBody}`;
  return `${real}+${imagBody}`;
}

function parenComplex(real: number, imag: number): string {
  return `(${complexTerm(real, imag)})`;
}

export const HIGH_SCHOOL_SAMPLERS: Record<string, Sampler> = {
  "algebra.linear-one-step"(rng, locale) {
    const a = randInt(rng, 2, 9);
    let x = randInt(rng, -5, 8);
    if (x === 0) x = 3;
    const b = randInt(rng, -8, 8);
    const c = a * x + b;
    return {
      prompt: text(locale, `Solve. ${linearExpr(a, b)} = ${c}`, `פתרו. ${linearExpr(a, b)} = ${c}`),
      expectedAnswer: `x=${x}`,
      acceptedAnswers: [String(x), `x = ${x}`],
      hints: lines(locale, [
        ["Undo the added term, then divide by the coefficient.", "בטלו את האיבר החופשי, ואז חלקו במקדם."],
        ["The coefficient stays on the variable until the last step.", "המקדם נשאר עם הנעלם עד השלב האחרון."],
      ]),
      explanation: text(
        locale,
        `Start from ${linearExpr(a, b)} = ${c}. Move the constant ${b} across the equals sign: ${a}x = ${c} - (${b}) = ${c - b}. Divide by ${a}: x=${x}.`,
        `מתחילים מ-${linearExpr(a, b)} = ${c}. מעבירים את הקבוע ${b} לאגף השני: ${a}x = ${c} - (${b}) = ${c - b}. מחלקים ב-${a}: x=${x}.`,
      ),
    };
  },

  "algebra.expand-binomial"(rng, locale) {
    const a = randInt(rng, -5, 6);
    const b = randInt(rng, -5, 6);
    const left = a === 0 ? "x" : a > 0 ? `(x+${a})` : `(x-${Math.abs(a)})`;
    const right = b === 0 ? "x" : b > 0 ? `(x+${b})` : `(x-${Math.abs(b)})`;
    return {
      prompt: text(locale, `Expand. ${left}${right}`, `פתחו. ${left}${right}`),
      expectedAnswer: poly([1, a + b, a * b]),
      acceptedAnswers: [],
      hints: lines(locale, [
        ["Multiply each term in the first factor by each term in the second.", "כפלו כל איבר בגורם הראשון בכל איבר בגורם השני."],
        ["Add the two middle products. They are the linear term.", "חברו את שתי המכפלות האמצעיות. הן האיבר הלינארי."],
      ]),
      explanation: text(
        locale,
        `Expand ${left}${right}. The square term is x^2. The cross terms add to ${a + b}, and the constants multiply to ${a * b}. The expansion is ${poly([1, a + b, a * b])}.`,
        `פותחים את ${left}${right}. איבר הריבוע הוא x^2. האיברים המוצלבים מסתכמים ל-${a + b}, והקבועים נכפלים ל-${a * b}. הפתיחה היא ${poly([1, a + b, a * b])}.`,
      ),
    };
  },

  "algebra.slope"(rng, locale) {
    const x1 = randInt(rng, -4, 4);
    const y1 = randInt(rng, -4, 4);
    const run = choice(rng, [-3, -2, -1, 1, 2, 3]);
    const rise = randInt(rng, -4, 4);
    const x2 = x1 + run;
    const y2 = y1 + rise;
    const g = gcd(rise, run);
    let num = rise / g;
    let den = run / g;
    if (den < 0) {
      num = -num;
      den = -den;
    }
    const expected = den === 1 ? String(num) : `${num}/${den}`;
    return {
      prompt: text(
        locale,
        `Slope through (${x1}, ${y1}) and (${x2}, ${y2}).`,
        `השיפוע דרך (${x1}, ${y1}) ו-(${x2}, ${y2}).`,
      ),
      expectedAnswer: expected,
      acceptedAnswers: [],
      hints: lines(locale, [
        ["Rise over run.", "ההפרש האנכי חלקי ההפרש האופקי."],
        ["Subtract the first point from the second, then reduce the fraction.", "חסרו את הנקודה הראשונה מהשנייה, ואז צמצמו את השבר."],
      ]),
      explanation: text(
        locale,
        `Slope through (${x1}, ${y1}) and (${x2}, ${y2}). Rise is ${y2} - (${y1}) = ${rise}. Run is ${x2} - (${x1}) = ${run}. The ratio ${rise}/${run} simplifies to ${expected}.`,
        `שיפוע דרך (${x1}, ${y1}) ו-(${x2}, ${y2}). השינוי האנכי הוא ${y2} - (${y1}) = ${rise}. השינוי האופקי הוא ${x2} - (${x1}) = ${run}. היחס ${rise}/${run} מצטמצם ל-${expected}.`,
      ),
    };
  },

  "algebra.system"(rng, locale) {
    const x = randInt(rng, -4, 4);
    const y = randInt(rng, -4, 4);
    const a1 = randInt(rng, 1, 4);
    const b1 = randInt(rng, 1, 4);
    let a2 = randInt(rng, 1, 4);
    let b2 = randInt(rng, 1, 4);
    if (a1 * b2 - a2 * b1 === 0) b2 += 1;
    const c1 = a1 * x + b1 * y;
    const c2 = a2 * x + b2 * y;
    const expected = `x=${x}, y=${y}`;
    return {
      prompt: text(
        locale,
        `Solve. ${a1}x + ${b1}y = ${c1} and ${a2}x + ${b2}y = ${c2}`,
        `פתרו. ${a1}x + ${b1}y = ${c1} וגם ${a2}x + ${b2}y = ${c2}`,
      ),
      expectedAnswer: expected,
      acceptedAnswers: [`x=${x},y=${y}`, `y=${y}, x=${x}`],
      hints: lines(locale, [
        ["Eliminate one variable, then back-substitute.", "חסלו נעלם אחד, ואז הציבו בחזרה."],
        ["Match one pair of coefficients, then add or subtract the equations.", "השוו זוג מקדמים, ואז חברו או חסרו את המשוואות."],
      ]),
      explanation: text(
        locale,
        `Multiply the first equation by ${b2} and the second by ${b1}, then subtract to clear y. The coefficient of x is ${a1}*${b2} - ${a2}*${b1} = ${a1 * b2 - a2 * b1}, and the constant side is ${c1}*${b2} - ${c2}*${b1} = ${c1 * b2 - c2 * b1}. So x=${x}. Substitute into the first equation to get y=${y}. The solution is ${expected}.`,
        `כופלים את המשוואה הראשונה ב-${b2} ואת השנייה ב-${b1}, ואז מחסרים כדי לבטל את y. המקדם של x הוא ${a1}*${b2} - ${a2}*${b1} = ${a1 * b2 - a2 * b1}, והאגף הקבוע הוא ${c1}*${b2} - ${c2}*${b1} = ${c1 * b2 - c2 * b1}. לכן x=${x}. מציבים במשוואה הראשונה ומקבלים y=${y}. הפתרון הוא ${expected}.`,
      ),
    };
  },

  "algebra.linear-inequality"(rng, locale) {
    const a = randInt(rng, 2, 6);
    const boundary = randInt(rng, -4, 6);
    const b = randInt(rng, -5, 5);
    const c = a * boundary + b;
    const strict = choice(rng, ["<", ">"] as const);
    const answer = strict === "<" ? `x<${boundary}` : `x>${boundary}`;
    return {
      prompt: text(
        locale,
        `Solve. ${linearExpr(a, b)} ${strict} ${c}`,
        `פתרו. ${linearExpr(a, b)} ${strict} ${c}`,
      ),
      expectedAnswer: answer,
      acceptedAnswers: [strict === "<" ? `x < ${boundary}` : `x > ${boundary}`],
      hints: lines(locale, [
        ["The coefficient of x is positive, so the inequality sign stays.", "המקדם של x חיובי, ולכן סימן האי-שוויון נשאר."],
        ["Undo the added term before dividing.", "בטלו את האיבר החופשי לפני החילוק."],
      ]),
      explanation: text(
        locale,
        `Start from ${linearExpr(a, b)} ${strict} ${c}. Move the constant: ${a}x ${strict} ${c - b}. Divide by ${a}. The sign stays because ${a} is positive. The solution is ${answer}.`,
        `מתחילים מ-${linearExpr(a, b)} ${strict} ${c}. מעבירים את הקבוע: ${a}x ${strict} ${c - b}. מחלקים ב-${a}. הסימן נשמר כי ${a} חיובי. הפתרון הוא ${answer}.`,
      ),
    };
  },

  "algebra.factor-quadratic"(rng, locale) {
    let p = randInt(rng, -6, 6);
    let q = randInt(rng, -6, 6);
    if (p === 0) p = 2;
    if (q === 0) q = 3;
    const expanded = poly([1, p + q, p * q]);
    return {
      prompt: text(locale, `Factor. ${expanded}`, `פרקו. ${expanded}`),
      expectedAnswer: `${parenFactor(p)}${parenFactor(q)}`,
      acceptedAnswers: p === q ? [] : [`${parenFactor(q)}${parenFactor(p)}`],
      hints: lines(locale, [
        ["Find two numbers whose sum and product match the polynomial.", "מצאו שני מספרים שסכומם ומכפלתם מתאימים לפולינום."],
        ["Those numbers are the constants inside the two linear factors.", "המספרים האלה הם הקבועים שבתוך שני הגורמים הלינאריים."],
      ]),
      explanation: text(
        locale,
        `For ${expanded}, the middle coefficient is ${p + q} and the constant term is ${p * q}. The pair ${p} and ${q} has that sum and that product. The factorization is ${parenFactor(p)}${parenFactor(q)}.`,
        `בפולינום ${expanded} המקדם האמצעי הוא ${p + q} והאיבר החופשי הוא ${p * q}. הזוג ${p} ו-${q} נותן את הסכום ואת המכפלה האלה. הפירוק הוא ${parenFactor(p)}${parenFactor(q)}.`,
      ),
    };
  },

  "algebra.vertex-form"(rng, locale) {
    const h = randInt(rng, -4, 4);
    const k = randInt(rng, -5, 5);
    const expanded = poly([1, -2 * h, h * h + k]);
    const square = h === 0 ? "x^2" : h > 0 ? `(x-${h})^2` : `(x+${-h})^2`;
    const form = k === 0 ? square : k > 0 ? `${square}+${k}` : `${square}${k}`;
    return {
      prompt: text(
        locale,
        `What is the vertex of y = ${expanded}?`,
        `מהו הקודקוד של y = ${expanded}?`,
      ),
      expectedAnswer: `(${h}, ${k})`,
      acceptedAnswers: [`(${h},${k})`],
      hints: lines(locale, [
        ["Complete the square, or read the vertex from vertex form.", "השלימו לריבוע, או קראו את הקודקוד מצורת הקודקוד."],
        ["The vertex is the point where the squared piece is zero.", "הקודקוד הוא הנקודה שבה הביטוי הריבועי מתאפס."],
      ]),
      explanation: text(
        locale,
        `Rewrite y = ${expanded} by completing the square. Vertex form is ${form}. The square is zero when x = ${h}, and the y-value there is ${k}. The vertex is (${h}, ${k}).`,
        `כותבים מחדש את y = ${expanded} על ידי השלמה לריבוע. צורת הקודקוד היא ${form}. הריבוע מתאפס כאשר x = ${h}, וערך y שם הוא ${k}. הקודקוד הוא (${h}, ${k}).`,
      ),
    };
  },

  "trig.exact-30-60"(rng, locale) {
    const item = choice(rng, EXACT_30_60);
    return {
      prompt: text(locale, `${item.fn} ${item.deg}° =`, `חשבו. ${item.fn} ${item.deg}° =`),
      expectedAnswer: item.expected,
      acceptedAnswers: [...item.accepted],
      hints: lines(locale, [
        ["Use a special right triangle.", "היעזרו במשולש ישר-זווית מיוחד."],
        ["Sine is opposite over hypotenuse. Cosine is adjacent over hypotenuse.", "סינוס הוא מול חלקי יתר. קוסינוס הוא ליד חלקי יתר."],
      ]),
      explanation: text(
        locale,
        `In a 30-60-90 triangle the short leg is half the hypotenuse and the long leg is that half times sqrt(3). ${item.fn} ${item.deg}° uses ${item.fn === "sin" ? "the opposite side" : "the adjacent side"}, so the exact value is ${item.expected}.`,
        `במשולש 30-60-90 הניצב הקצר הוא חצי מהיתר, והניצב הארוך הוא החצי הזה כפול sqrt(3). ${item.fn} ${item.deg}° משתמש ב${item.fn === "sin" ? "צלע שמול" : "צלע שליד"}, ולכן הערך המדויק הוא ${item.expected}.`,
      ),
    };
  },

  "trig.exact-axis"(rng, locale) {
    const item = choice(rng, AXIS);
    return {
      prompt: text(locale, `${item.fn} ${item.deg}° =`, `חשבו. ${item.fn} ${item.deg}° =`),
      expectedAnswer: item.expected,
      acceptedAnswers: [],
      hints: lines(locale, [
        ["Read the value on the unit circle axis.", "קראו את הערך על ציר מעגל היחידה."],
        ["Cosine is the horizontal coordinate and sine is the vertical coordinate.", "קוסינוס הוא השיעור האופקי וסינוס הוא השיעור האנכי."],
      ]),
      explanation: text(
        locale,
        `On the unit circle, ${item.fn} reads the ${item.fn === "cos" ? "x" : "y"}-coordinate. At ${item.deg}° that coordinate is ${item.expected}.`,
        `במעגל היחידה, ${item.fn} קורא את שיעור ${item.fn === "cos" ? "x" : "y"}. ב-${item.deg}° השיעור הזה הוא ${item.expected}.`,
      ),
    };
  },

  "trig.tan-special"(rng, locale) {
    const deg = choice(rng, [45, 135]);
    const expected = deg === 45 ? "1" : "-1";
    return {
      prompt: text(locale, `tan ${deg}° =`, `חשבו. tan ${deg}° =`),
      expectedAnswer: expected,
      acceptedAnswers: [],
      hints: lines(locale, [
        ["Tangent is sine over cosine.", "טנגנס הוא סינוס חלקי קוסינוס."],
        ["At these angles the two coordinates have the same size.", "בזוויות האלה לשני השיעורים אותו גודל."],
      ]),
      explanation: text(
        locale,
        `tan ${deg}° = sin ${deg}° / cos ${deg}°. Those two values have the same size${deg === 135 ? " and opposite signs" : ""}, so the quotient is ${expected}.`,
        `tan ${deg}° = sin ${deg}° / cos ${deg}°. לשני הערכים אותו גודל${deg === 135 ? " וסימנים הפוכים" : ""}, ולכן המנה היא ${expected}.`,
      ),
    };
  },

  "trig.right-ratio"(rng, locale) {
    const [a, b, c] = choice(rng, TRIPLES);
    const ratio = choice(rng, ["sin", "cos", "tan"] as const);
    const opposite = a;
    const adjacent = b;
    const expected =
      ratio === "sin" ? `${opposite}/${c}` : ratio === "cos" ? `${adjacent}/${c}` : `${opposite}/${adjacent}`;
    const g = gcd(Number(expected.split("/")[0]), Number(expected.split("/")[1]));
    const simple = `${Number(expected.split("/")[0]) / g}/${Number(expected.split("/")[1]) / g}`;
    return {
      prompt: text(
        locale,
        `In a right triangle the opposite side is ${opposite}, the adjacent side is ${adjacent}, and the hypotenuse is ${c}. ${ratio} =`,
        `במשולש ישר-זווית הניצב שמול הזווית הוא ${opposite}, הניצב שלידה הוא ${adjacent}, והיתר הוא ${c}. ${ratio} =`,
      ),
      expectedAnswer: simple,
      acceptedAnswers: simple === expected ? [] : [expected],
      hints: lines(locale, [
        ["Choose the two sides the ratio names.", "בחרו את שתי הצלעות שהיחס מציין."],
        ["Sine uses the opposite side, cosine the adjacent side, and tangent both of those.", "סינוס משתמש בצלע שמול, קוסינוס בצלע שליד, וטנגנס בשתיהן."],
      ]),
      explanation: text(
        locale,
        `${ratio} compares ${ratio === "sin" ? `${opposite} and ${c}` : ratio === "cos" ? `${adjacent} and ${c}` : `${opposite} and ${adjacent}`}. Reduce if possible. The ratio is ${simple}.`,
        `${ratio} משווה את ${ratio === "sin" ? `${opposite} ו-${c}` : ratio === "cos" ? `${adjacent} ו-${c}` : `${opposite} ו-${adjacent}`}. מצמצמים אם אפשר. היחס הוא ${simple}.`,
      ),
    };
  },

  "trig.inverse-special"(rng, locale) {
    const rows = [
      { fn: "arcsin", arg: "1/2", deg: 30 },
      { fn: "arcsin", arg: "sqrt(2)/2", deg: 45 },
      { fn: "arcsin", arg: "sqrt(3)/2", deg: 60 },
      { fn: "arccos", arg: "1/2", deg: 60 },
      { fn: "arccos", arg: "sqrt(2)/2", deg: 45 },
      { fn: "arccos", arg: "sqrt(3)/2", deg: 30 },
    ] as const;
    const item = choice(rng, rows);
    return {
      prompt: text(locale, `${item.fn}(${item.arg}) in degrees =`, `${item.fn}(${item.arg}) במעלות =`),
      expectedAnswer: String(item.deg),
      acceptedAnswers: [`${item.deg}°`, `${item.deg} degrees`],
      hints: lines(locale, [
        ["Give the degree measure of the special angle.", "תנו את מידת המעלות של הזווית המיוחדת."],
        ["Inverse sine and inverse cosine return the acute angle with that ratio.", "סינוס הפוך וקוסינוס הפוך מחזירים את הזווית החדה עם היחס הזה."],
      ]),
      explanation: text(
        locale,
        `${item.fn}(${item.arg}) asks for the acute angle whose ${item.fn === "arcsin" ? "sine" : "cosine"} is ${item.arg}. That special angle measures ${item.deg} degrees.`,
        `${item.fn}(${item.arg}) שואל מהי הזווית החדה שה${item.fn === "arcsin" ? "סינוס" : "קוסינוס"} שלה הוא ${item.arg}. הזווית המיוחדת הזו היא ${item.deg} מעלות.`,
      ),
    };
  },

  "calc.power-rule"(rng, locale) {
    const a = randInt(rng, 1, 6);
    const n = randInt(rng, 2, 5);
    const coeff = a * n;
    const next = n - 1;
    const stem = a === 1 ? `x^${n}` : `${a}x^${n}`;
    const expected = next === 1
      ? (coeff === 1 ? "x" : `${coeff}x`)
      : (coeff === 1 ? `x^${next}` : `${coeff}x^${next}`);
    return {
      prompt: text(locale, `Differentiate. ${stem}`, `גזרו. ${stem}`),
      expectedAnswer: expected,
      acceptedAnswers: [],
      hints: lines(locale, [
        ["Bring the exponent to the front and reduce it.", "הורידו את החזקה והכפילו בה."],
        ["A constant multiplier stays in front while the power changes.", "מקדם קבוע נשאר מלפנים בזמן שהחזקה משתנה."],
      ]),
      explanation: text(
        locale,
        `Differentiate ${stem}. Multiply the coefficient ${a} by the exponent ${n} to get ${coeff}, then lower the exponent from ${n} to ${next}. The derivative is ${expected}.`,
        `גוזרים את ${stem}. כופלים את המקדם ${a} במעריך ${n} ומקבלים ${coeff}, ואז מורידים את המעריך מ-${n} ל-${next}. הנגזרת היא ${expected}.`,
      ),
    };
  },

  "calc.product-rule"(rng, locale) {
    const a = randInt(rng, 1, 4);
    const b = randInt(rng, -4, 4);
    const c = randInt(rng, 1, 4);
    const d = randInt(rng, -4, 4);
    const coeff = 2 * a * c;
    const constant = a * d + b * c;
    return {
      prompt: text(
        locale,
        `Differentiate. (${linearExpr(a, b)})(${linearExpr(c, d)})`,
        `גזרו. (${linearExpr(a, b)})(${linearExpr(c, d)})`,
      ),
      expectedAnswer: poly([coeff, constant]),
      acceptedAnswers: [],
      hints: lines(locale, [
        ["Differentiate one factor at a time and add.", "גזרו גורם אחד בכל פעם וחברו."],
        ["Each term is one derivative times the other original factor.", "כל מחובר הוא נגזרת של גורם אחד כפול הגורם השני המקורי."],
      ]),
      explanation: text(
        locale,
        `Let u = ${linearExpr(a, b)} and v = ${linearExpr(c, d)}. Then u' = ${a} and v' = ${c}. u'v + uv' expands to ${poly([coeff, constant])}.`,
        `נסמן u = ${linearExpr(a, b)} ו-v = ${linearExpr(c, d)}. אז u' = ${a} ו-v' = ${c}. הפתיחה של u'v + uv' היא ${poly([coeff, constant])}.`,
      ),
    };
  },

  "calc.chain-rule"(rng, locale) {
    const a = randInt(rng, 1, 4);
    const b = randInt(rng, -4, 4);
    const n = randInt(rng, 2, 4);
    const coeff = n * a;
    const inside = linearExpr(a, b);
    const power = n - 1;
    const expected = power === 1
      ? (coeff === 1 ? `(${inside})` : `${coeff}(${inside})`)
      : (coeff === 1 ? `(${inside})^${power}` : `${coeff}(${inside})^${power}`);
    return {
      prompt: text(
        locale,
        `Differentiate. (${inside})^${n}`,
        `גזרו. (${inside})^${n}`,
      ),
      expectedAnswer: expected,
      acceptedAnswers: [],
      hints: lines(locale, [
        ["Differentiate the outside, then multiply by the inside derivative.", "גזרו את החוץ, ואז כפלו בנגזרת הפנים."],
        ["The inside derivative is the coefficient of the linear expression.", "נגזרת הפנים היא המקדם של הביטוי הלינארי."],
      ]),
      explanation: text(
        locale,
        `The outside power is ${n}, so it comes down and drops by one. Multiply by the inside derivative ${a}: ${n} * ${a} = ${coeff}. The derivative is ${expected}.`,
        `החזקה החיצונית היא ${n}, לכן מורידים אותה ומקטינים באחד. כופלים בנגזרת הפנימית ${a}: ${n} * ${a} = ${coeff}. הנגזרת היא ${expected}.`,
      ),
    };
  },

  "calc.indefinite-integral"(rng, locale) {
    const n = randInt(rng, 1, 4);
    const k = randInt(rng, 1, 4);
    const a = k * (n + 1);
    const power = n + 1;
    const expected = k === 1 ? `x^${power}+C` : `${k}x^${power}+C`;
    const stem = a === 1 ? `x^${n}` : `${a}x^${n}`;
    return {
      prompt: text(locale, `Integrate. ${stem}`, `חשבו אינטגרל. ${stem}`),
      expectedAnswer: expected,
      acceptedAnswers: [`${expected.slice(0, -2)}+c`],
      hints: lines(locale, [
        ["Raise the power, divide by the new power, and add the constant.", "העלו את החזקה, חלקו בחזקה החדשה, והוסיפו את הקבוע."],
        ["The constant is required on an indefinite integral.", "הקבוע נדרש באינטגרל לא מסוים."],
      ]),
      explanation: text(
        locale,
        `Integrate ${stem}. The new power is ${power}, and ${a} / ${power} = ${k}. Attach the constant of integration. The antiderivative is ${expected}.`,
        `מחשבים אינטגרל של ${stem}. החזקה החדשה היא ${power}, ו-${a} / ${power} = ${k}. מוסיפים קבוע אינטגרציה. הפונקציה הקדומה היא ${expected}.`,
      ),
    };
  },

  "calc.definite-integral"(rng, locale) {
    const n = randInt(rng, 1, 3);
    const k = randInt(rng, 1, 3);
    const m = randInt(rng, 1, 3);
    const a = k * (n + 1);
    const value = k * m ** (n + 1);
    const stem = a === 1 ? `x^${n}` : `${a}x^${n}`;
    return {
      prompt: text(
        locale,
        `Evaluate the integral from 0 to ${m} of ${stem}.`,
        `חשבו את האינטגרל מ-0 עד ${m} של ${stem}.`,
      ),
      expectedAnswer: String(value),
      acceptedAnswers: [],
      hints: lines(locale, [
        ["Use the antiderivative, then the limits.", "השתמשו בפונקציה הקדומה, ואז בגבולות."],
        ["Subtract the value at the lower limit from the value at the upper limit.", "חסרו את הערך בגבול התחתון מהערך בגבול העליון."],
      ]),
      explanation: text(
        locale,
        `An antiderivative of ${stem} is ${k === 1 ? `x^${n + 1}` : `${k}x^${n + 1}`}. At the upper limit ${m} it equals ${value}. At the lower limit 0 it equals 0. The value of the integral is ${value}.`,
        `פונקציה קדומה של ${stem} היא ${k === 1 ? `x^${n + 1}` : `${k}x^${n + 1}`}. בגבול העליון ${m} מתקבל ${value}. בגבול התחתון 0 מתקבל 0. ערך האינטגרל הוא ${value}.`,
      ),
    };
  },

  "vectors.add"(rng, locale) {
    const a = randInt(rng, -6, 6);
    const b = randInt(rng, -6, 6);
    const c = randInt(rng, -6, 6);
    const d = randInt(rng, -6, 6);
    const x = a + c;
    const y = b + d;
    return {
      prompt: text(
        locale,
        `Add. (${a}, ${b}) + (${c}, ${d})`,
        `חברו. (${a}, ${b}) + (${c}, ${d})`,
      ),
      expectedAnswer: `(${x}, ${y})`,
      acceptedAnswers: [`(${x},${y})`],
      hints: lines(locale, [
        ["Add the matching components.", "חברו רכיבים תואמים."],
        ["The first components add together, and the second components add together.", "הרכיבים הראשונים מתחברים, והרכיבים השניים מתחברים."],
      ]),
      explanation: text(
        locale,
        `Add matching components: ${a} + ${c} = ${x} and ${b} + ${d} = ${y}. The sum is (${x}, ${y}).`,
        `מחברים רכיבים תואמים: ${a} + ${c} = ${x} ו-${b} + ${d} = ${y}. הסכום הוא (${x}, ${y}).`,
      ),
    };
  },

  "vectors.magnitude"(rng, locale) {
    const [a, b, c] = choice(rng, TRIPLES);
    const flipA = choice(rng, [1, -1]);
    const flipB = choice(rng, [1, -1]);
    const x = a * flipA;
    const y = b * flipB;
    return {
      prompt: text(locale, `Magnitude of (${x}, ${y}).`, `האורך של (${x}, ${y}).`),
      expectedAnswer: String(c),
      acceptedAnswers: [],
      hints: lines(locale, [
        ["Use the Pythagorean theorem on the components.", "השתמשו במשפט פיתגורס על הרכיבים."],
        ["Square both components, add, then take the square root.", "ריבעו את שני הרכיבים, חברו, ואז הוציאו שורש."],
      ]),
      explanation: text(
        locale,
        `Square the components: ${x}^2 + ${y}^2 = ${x * x} + ${y * y} = ${c * c}. The square root is ${c}. The magnitude is ${c}.`,
        `מרבעים את הרכיבים: ${x}^2 + ${y}^2 = ${x * x} + ${y * y} = ${c * c}. השורש הוא ${c}. האורך הוא ${c}.`,
      ),
    };
  },

  "vectors.scalar"(rng, locale) {
    const k = choice(rng, [-3, -2, 2, 3, 4]);
    const a = randInt(rng, -5, 5);
    const b = randInt(rng, -5, 5);
    return {
      prompt: text(locale, `Compute. ${k}(${a}, ${b})`, `חשבו. ${k}(${a}, ${b})`),
      expectedAnswer: `(${k * a}, ${k * b})`,
      acceptedAnswers: [`(${k * a},${k * b})`],
      hints: lines(locale, [
        ["Multiply each component by the scalar.", "כפלו כל רכיב בסקלר."],
        ["Every component is scaled by the same factor.", "כל רכיב מוכפל באותו גורם."],
      ]),
      explanation: text(
        locale,
        `Multiply each component by ${k}: ${k} * ${a} = ${k * a} and ${k} * ${b} = ${k * b}. The result is (${k * a}, ${k * b}).`,
        `כופלים כל רכיב ב-${k}: ${k} * ${a} = ${k * a} ו-${k} * ${b} = ${k * b}. התוצאה היא (${k * a}, ${k * b}).`,
      ),
    };
  },

  "vectors.dot"(rng, locale) {
    const a = randInt(rng, -5, 5);
    const b = randInt(rng, -5, 5);
    const c = randInt(rng, -5, 5);
    const d = randInt(rng, -5, 5);
    return {
      prompt: text(
        locale,
        `Dot product. (${a}, ${b}) · (${c}, ${d})`,
        `מכפלה סקלרית. (${a}, ${b}) · (${c}, ${d})`,
      ),
      expectedAnswer: String(a * c + b * d),
      acceptedAnswers: [],
      hints: lines(locale, [
        ["Multiply matching components, then add.", "כפלו רכיבים תואמים, ואז חברו."],
        ["The result is a single number, not a vector.", "התוצאה היא מספר יחיד, לא וקטור."],
      ]),
      explanation: text(
        locale,
        `Multiply matching components: ${a} * ${c} = ${a * c} and ${b} * ${d} = ${b * d}. Add them: ${a * c} + ${b * d} = ${a * c + b * d}.`,
        `כופלים רכיבים תואמים: ${a} * ${c} = ${a * c} ו-${b} * ${d} = ${b * d}. מחברים: ${a * c} + ${b * d} = ${a * c + b * d}.`,
      ),
    };
  },

  "complex.powers-of-i"(rng, locale) {
    const n = randInt(rng, 1, 8);
    const cycle = ["0+i", "-1", "-i", "1"] as const;
    const value = cycle[(n - 1) % 4];
    const remainder = n % 4;
    return {
      prompt: text(locale, `Simplify. i^${n}`, `פשטו. i^${n}`),
      expectedAnswer: value,
      acceptedAnswers: [],
      hints: lines(locale, [
        ["The cycle has four steps.", "המחזור בן ארבעה צעדים."],
        ["After four powers the value returns to one, so group the exponent by fours.", "אחרי ארבע חזקות הערך חוזר לאחד, לכן מקבצים את המעריך ברביעיות."],
      ]),
      explanation: text(
        locale,
        `Powers of i cycle every four exponents: remainder 1 gives 0+i, remainder 2 gives -1, remainder 3 gives -i, and remainder 0 gives 1. The exponent ${n} leaves remainder ${remainder}, so i^${n} = ${value}.`,
        `חזקות של i חוזרות כל ארבעה מעריכים: שארית 1 נותנת 0+i, שארית 2 נותנת -1, שארית 3 נותנת -i, ושארית 0 נותנת 1. למעריך ${n} נשארת שארית ${remainder}, לכן i^${n} = ${value}.`,
      ),
    };
  },

  "complex.add"(rng, locale) {
    const a = randInt(rng, -6, 6);
    const b = randInt(rng, -6, 6);
    const c = randInt(rng, -6, 6);
    const d = randInt(rng, -6, 6);
    return {
      prompt: text(
        locale,
        `Add. ${parenComplex(a, b)} + ${parenComplex(c, d)}`,
        `חברו. ${parenComplex(a, b)} + ${parenComplex(c, d)}`,
      ),
      expectedAnswer: complexTerm(a + c, b + d),
      acceptedAnswers: [],
      hints: lines(locale, [
        ["Add the real parts and the imaginary parts.", "חברו את החלקים הממשיים ואת החלקים המדומים."],
        ["Keep the real numbers together and the coefficients of i together.", "השאירו את המספרים הממשיים יחד ואת המקדמים של i יחד."],
      ]),
      explanation: text(
        locale,
        `Add the real parts: ${a} + ${c} = ${a + c}. Add the imaginary parts: ${b} + ${d} = ${b + d}. The sum is ${complexTerm(a + c, b + d)}.`,
        `מחברים את החלקים הממשיים: ${a} + ${c} = ${a + c}. מחברים את החלקים המדומים: ${b} + ${d} = ${b + d}. הסכום הוא ${complexTerm(a + c, b + d)}.`,
      ),
    };
  },

  "complex.modulus"(rng, locale) {
    const [a0, b0, c] = choice(rng, TRIPLES);
    const a = a0 * choice(rng, [1, -1]);
    const b = b0 * choice(rng, [1, -1]);
    return {
      prompt: text(
        locale,
        `Modulus of ${complexTerm(a, b)}.`,
        `המודול של ${complexTerm(a, b)}.`,
      ),
      expectedAnswer: String(c),
      acceptedAnswers: [],
      hints: lines(locale, [
        ["Square both parts, add, then take the square root.", "ריבעו את שני החלקים, חברו, ואז הוציאו שורש."],
        ["The modulus is a distance, so it is never negative.", "המודול הוא מרחק, ולכן הוא אינו שלילי."],
      ]),
      explanation: text(
        locale,
        `Square both parts: ${a}^2 + ${b}^2 = ${a * a} + ${b * b} = ${c * c}. The square root is ${c}. The modulus is ${c}.`,
        `מרבעים את שני החלקים: ${a}^2 + ${b}^2 = ${a * a} + ${b * b} = ${c * c}. השורש הוא ${c}. המודול הוא ${c}.`,
      ),
    };
  },

  "algebra.exponent-product"(rng, locale) {
    const m = randInt(rng, 2, 6);
    const n = randInt(rng, 2, 6);
    const withCoeff = rng() < 0.5;
    const a = withCoeff ? randInt(rng, 2, 5) : 1;
    const b = withCoeff ? randInt(rng, 2, 5) : 1;
    const coeff = a * b;
    const exp = m + n;
    const left = a === 1 ? `x^${m}` : `${a}x^${m}`;
    const right = b === 1 ? `x^${n}` : `${b}x^${n}`;
    const expected = coeff === 1 ? `x^${exp}` : `${coeff}x^${exp}`;
    const accepted = new Set<string>([coeff === 1 ? `x^(${exp})` : `${coeff}x^(${exp})`]);
    if (coeff !== 1) accepted.add(`${coeff}*x^${exp}`);
    return {
      prompt: text(locale, `Simplify. ${left} * ${right}`, `פשטו. ${left} * ${right}`),
      expectedAnswer: expected,
      acceptedAnswers: [...accepted],
      hints: lines(locale, [
        ["When the bases match, add the exponents.", "כשהבסיסים שווים, מחברים את המעריכים."],
        ["A coefficient in front is multiplied separately from the exponents.", "מקדם מלפנים נכפל בנפרד מן המעריכים."],
      ]),
      explanation: text(
        locale,
        `The bases match, so add the exponents: ${m} + ${n} = ${exp}.${coeff === 1 ? "" : ` Multiply the coefficients: ${a} * ${b} = ${coeff}.`} The product is ${expected}.`,
        `הבסיסים שווים, לכן מחברים את המעריכים: ${m} + ${n} = ${exp}.${coeff === 1 ? "" : ` כופלים את המקדמים: ${a} * ${b} = ${coeff}.`} המכפלה היא ${expected}.`,
      ),
    };
  },

  "algebra.exponent-power"(rng, locale) {
    const m = randInt(rng, 2, 4);
    const n = randInt(rng, 2, 3);
    const a = rng() < 0.5 ? choice(rng, [2, 3]) : 1;
    const coeff = repeatMultiply(1, a, n);
    const exp = m * n;
    const inside = a === 1 ? `x^${m}` : `${a}x^${m}`;
    const expected = coeff === 1 ? `x^${exp}` : `${coeff}x^${exp}`;
    const accepted = [coeff === 1 ? `x^(${exp})` : `${coeff}x^(${exp})`];
    if (coeff !== 1) accepted.push(`${coeff}*x^${exp}`);
    return {
      prompt: text(locale, `Simplify. (${inside})^${n}`, `פשטו. (${inside})^${n}`),
      expectedAnswer: expected,
      acceptedAnswers: accepted,
      hints: lines(locale, [
        ["A power outside parentheses multiplies the exponents inside.", "חזקה מחוץ לסוגריים כופלת את המעריכים שבפנים."],
        ["A coefficient inside is raised to that same outside power.", "מקדם שבפנים מועלה באותה חזקה חיצונית."],
      ]),
      explanation: text(
        locale,
        `Multiply the exponents: ${m} * ${n} = ${exp}.${a === 1 ? "" : ` Raise the coefficient: ${a}^${n} = ${coeff}.`} The result is ${expected}.`,
        `כופלים את המעריכים: ${m} * ${n} = ${exp}.${a === 1 ? "" : ` מעלים את המקדם: ${a}^${n} = ${coeff}.`} התוצאה היא ${expected}.`,
      ),
    };
  },

  "algebra.zero-negative-exponent"(rng, locale) {
    const kind = choice(rng, [0, -1, -2] as const);
    const base = kind === -2 ? randInt(rng, 2, 6) : randInt(rng, 2, 9);
    const expected = kind === 0 ? "1" : kind === -1 ? `1/${base}` : `1/${base * base}`;
    const why =
      kind === 0
        ? `Any nonzero number to the power zero is 1, so ${base}^(0) = 1.`
        : kind === -1
          ? `A negative exponent takes the reciprocal. ${base}^(-1) = 1/${base}.`
          : `A negative exponent takes the reciprocal and the exponent becomes positive. ${base}^(-2) = 1/${base}^2 = 1/${base * base}.`;
    const whyHe =
      kind === 0
        ? `כל מספר שונה מאפס בחזקת אפס שווה 1, לכן ${base}^(0) = 1.`
        : kind === -1
          ? `מעריך שלילי הופך את המספר לשבר הופכי. ${base}^(-1) = 1/${base}.`
          : `מעריך שלילי הופך את המספר לשבר הופכי, והמעריך נעשה חיובי. ${base}^(-2) = 1/${base}^2 = 1/${base * base}.`;
    return {
      prompt: text(locale, `Simplify. ${base}^(${kind})`, `פשטו. ${base}^(${kind})`),
      expectedAnswer: expected,
      acceptedAnswers: [],
      hints: lines(locale, [
        ["A nonzero number to the power zero is one.", "מספר שונה מאפס בחזקת אפס שווה אחד."],
        ["A negative exponent writes the reciprocal, and the exponent becomes positive.", "מעריך שלילי נכתב כשבר הופכי, והמעריך נעשה חיובי."],
      ]),
      explanation: text(locale, why, whyHe),
    };
  },

  "algebra.function-value"(rng, locale) {
    const t = randInt(rng, -3, 5);
    const quadratic = rng() < 0.5;
    let expr = "";
    let value = 0;
    let steps = "";
    let stepsHe = "";
    if (quadratic) {
      const a = randInt(rng, 1, 3);
      const b = randInt(rng, -3, 3);
      const c = randInt(rng, -5, 5);
      expr = formatQuadraticFn(a, b, c);
      value = a * t * t + b * t + c;
      steps = `Substitute ${t} for x: ${a}*(${t})^2 + ${b}*(${t}) + ${c} = ${value}.`;
      stepsHe = `מציבים ${t} במקום x: ${a}*(${t})^2 + ${b}*(${t}) + ${c} = ${value}.`;
    } else {
      let a = randInt(rng, -4, 5);
      if (a === 0) a = 2;
      const b = randInt(rng, -6, 6);
      expr = formatLinearFn(a, b);
      value = a * t + b;
      steps = `Substitute ${t} for x: ${a}*(${t}) + ${b} = ${value}.`;
      stepsHe = `מציבים ${t} במקום x: ${a}*(${t}) + ${b} = ${value}.`;
    }
    return {
      prompt: text(
        locale,
        `Find f(${t}) for f(x) = ${expr}.`,
        `מצאו את f(${t}) עבור f(x) = ${expr}.`,
      ),
      expectedAnswer: String(value),
      acceptedAnswers: [`f(${t})=${value}`, `f(${t}) = ${value}`],
      hints: lines(locale, [
        ["Replace the variable with the given input.", "החליפו את המשתנה בקלט הנתון."],
        ["Follow the order of operations: powers, then multiplication, then addition.", "שמרו על סדר הפעולות: קודם חזקות, אחר כך כפל, ואז חיבור."],
      ]),
      explanation: text(
        locale,
        `f(x) = ${expr}. ${steps}`,
        `f(x) = ${expr}. ${stepsHe}`,
      ),
    };
  },

  "algebra.solve-quadratic"(rng, locale) {
    let r = randInt(rng, -6, 6);
    let s = rng() < 0.25 ? r : randInt(rng, -6, 6);
    if (r === 0 && s === 0) r = 2;
    const expanded = poly([1, -(r + s), r * s]);
    const lo = Math.min(r, s);
    const hi = Math.max(r, s);
    const expected = r === s ? `x=${r}` : `x=${lo}, x=${hi}`;
    const factors =
      r === s ? `${parenFactor(-r)}^2` : `${parenFactor(-r)}${parenFactor(-s)}`;
    const accepted =
      r === s
        ? [`x=${r}, x=${r}`]
        : [`x=${hi}, x=${lo}`, `x=${r}, x=${s}`, `x=${s}, x=${r}`];
    return {
      prompt: text(locale, `Solve. ${expanded} = 0`, `פתרו. ${expanded} = 0`),
      expectedAnswer: expected,
      acceptedAnswers: accepted,
      hints: lines(locale, [
        ["Find two numbers with the polynomial's sum and product, then set each factor equal to zero.", "מצאו שני מספרים עם הסכום והמכפלה של הפולינום, ואז השוו כל גורם לאפס."],
        [
          r === s
            ? "A repeated factor still gives only one distinct solution."
            : "Each linear factor gives one solution.",
          r === s
            ? "גורם חוזר עדיין נותן פתרון אחד בלבד."
            : "כל גורם לינארי נותן פתרון אחד.",
        ],
      ]),
      explanation: text(
        locale,
        r === s
          ? `${expanded} = 0 factors as ${factors} = 0 because the root ${r} is repeated. Set the factor equal to zero: x=${r}. The solution is ${expected}.`
          : `${expanded} = 0 factors as ${factors} = 0 because ${r} and ${s} add to ${r + s} and multiply to ${r * s}. Set each factor equal to zero: x=${r} or x=${s}. In order, the solutions are ${expected}.`,
        r === s
          ? `${expanded} = 0 מתפרק ל-${factors} = 0 כי השורש ${r} חוזר. משווים את הגורם לאפס: x=${r}. הפתרון הוא ${expected}.`
          : `${expanded} = 0 מתפרק ל-${factors} = 0 כי ${r} ו-${s} מסתכמים ל-${r + s} ומכפלתם ${r * s}. משווים כל גורם לאפס: x=${r} או x=${s}. לפי הסדר, הפתרונות הם ${expected}.`,
      ),
    };
  },

  "algebra.logarithm"(rng, locale) {
    const pick = choice(rng, [
      { base: 2, max: 6 },
      { base: 3, max: 4 },
      { base: 5, max: 3 },
      { base: 10, max: 3 },
    ] as const);
    const usePowerRule = rng() < 0.55;
    if (usePowerRule) {
      const innerExp = randInt(rng, 1, Math.min(3, pick.max));
      const outerExp = randInt(rng, 2, 4);
      const inner = repeatMultiply(1, pick.base, innerExp);
      const answer = innerExp * outerExp;
      return {
        prompt: text(
          locale,
          `Evaluate. log_${pick.base}(${inner}^${outerExp})`,
          `חשבו. log_${pick.base}(${inner}^${outerExp})`,
        ),
        expectedAnswer: String(answer),
        acceptedAnswers: [`log_${pick.base}(${inner}^${outerExp})=${answer}`],
        hints: lines(locale, [
          ["The power rule moves the outer exponent in front of the logarithm.", "כלל החזקה מעביר את המעריך החיצוני לפני הלוגריתם."],
          ["Then evaluate the remaining logarithm of the inner base power.", "ואז חשבו את הלוגריתם שנשאר של חזקת הבסיס הפנימית."],
        ]),
        explanation: text(
          locale,
          `log_${pick.base}(${inner}^${outerExp}) = ${outerExp} · log_${pick.base}(${inner}). Because ${pick.base}^${innerExp} = ${inner}, log_${pick.base}(${inner}) = ${innerExp}. Multiply: ${outerExp} · ${innerExp} = ${answer}.`,
          `log_${pick.base}(${inner}^${outerExp}) = ${outerExp} · log_${pick.base}(${inner}). מכיוון ש-${pick.base}^${innerExp} = ${inner}, מתקיים log_${pick.base}(${inner}) = ${innerExp}. כופלים: ${outerExp} · ${innerExp} = ${answer}.`,
        ),
      };
    }
    const k = randInt(rng, 1, pick.max);
    const value = repeatMultiply(1, pick.base, k);
    return {
      prompt: text(
        locale,
        `Evaluate. log_${pick.base}(${value})`,
        `חשבו. log_${pick.base}(${value})`,
      ),
      expectedAnswer: String(k),
      acceptedAnswers: [`log_${pick.base}(${value})=${k}`],
      hints: lines(locale, [
        ["A logarithm asks for the exponent that produces the argument from the base.", "לוגריתם שואל איזה מעריך יוצר את הארגומנט מן הבסיס."],
        ["Write the argument as a power of the base, then read that exponent.", "כתבו את הארגומנט כחזקה של הבסיס, ואז קראו את המעריך."],
      ]),
      explanation: text(
        locale,
        `log_${pick.base}(${value}) asks which power of ${pick.base} equals ${value}. ${pick.base}^${k} = ${value}, so the logarithm is ${k}.`,
        `log_${pick.base}(${value}) שואל איזו חזקה של ${pick.base} שווה ל-${value}. ${pick.base}^${k} = ${value}, לכן הלוגריתם הוא ${k}.`,
      ),
    };
  },

  "sequences.arithmetic-term"(rng, locale) {
    const a1 = randInt(rng, -6, 12);
    let d = randInt(rng, -5, 6);
    if (d === 0) d = 3;
    const n = randInt(rng, 4, 8);
    const jumps = n - 1;
    const term = a1 + jumps * d;
    return {
      prompt: text(
        locale,
        `Arithmetic sequence with a1 = ${a1}, common difference d = ${d}. Find term n = ${n}.`,
        `סדרה חשבונית עם a1 = ${a1}, הפרש קבוע d = ${d}. מצאו את האיבר n = ${n}.`,
      ),
      expectedAnswer: String(term),
      acceptedAnswers: [`a${n}=${term}`, `a_${n}=${term}`],
      hints: lines(locale, [
        ["The nth term is the first term plus the difference once for each step after the first.", "האיבר ה-n הוא האיבר הראשון ועוד ההפרש פעם אחת לכל צעד אחרי הראשון."],
        ["Count how many steps separate the first term from the term you want.", "ספרו כמה צעדים מפרידים בין האיבר הראשון לאיבר המבוקש."],
      ]),
      explanation: text(
        locale,
        `There are ${jumps} steps after the first term. ${jumps} * ${d} = ${jumps * d}. Add the first term: ${a1} + ${jumps * d} = ${term}.`,
        `יש ${jumps} צעדים אחרי האיבר הראשון. ${jumps} * ${d} = ${jumps * d}. מוסיפים את האיבר הראשון: ${a1} + ${jumps * d} = ${term}.`,
      ),
    };
  },

  "sequences.geometric-term"(rng, locale) {
    const a1 = randInt(rng, 1, 5);
    const r = choice(rng, [-3, -2, 2, 3]);
    const n = randInt(rng, 3, 5);
    const jumps = n - 1;
    const power = repeatMultiply(1, r, jumps);
    const term = a1 * power;
    return {
      prompt: text(
        locale,
        `Geometric sequence with a1 = ${a1}, common ratio r = ${r}. Find term n = ${n}.`,
        `סדרה הנדסית עם a1 = ${a1}, מנה r = ${r}. מצאו את האיבר n = ${n}.`,
      ),
      expectedAnswer: String(term),
      acceptedAnswers: [`a${n}=${term}`, `a_${n}=${term}`],
      hints: lines(locale, [
        ["Each new term multiplies the previous term by the common ratio.", "כל איבר חדש כופל את האיבר הקודם במנה."],
        ["Start at the first term and multiply once for each step after the first.", "מתחילים באיבר הראשון וכופלים פעם אחת לכל צעד אחרי הראשון."],
      ]),
      explanation: text(
        locale,
        `The exponent on the ratio is ${jumps}. ${powerExpr(r, jumps)} = ${power}. Multiply by the first term: ${a1} * ${power} = ${term}.`,
        `המעריך על המנה הוא ${jumps}. ${powerExpr(r, jumps)} = ${power}. כופלים באיבר הראשון: ${a1} * ${power} = ${term}.`,
      ),
    };
  },

  "trig.special-right"(rng, locale) {
    const kind = choice(rng, [
      "hyp-from-short",
      "short-from-hyp",
      "long-from-short",
      "short-from-long",
      "long-from-hyp",
      "hyp-from-long",
      "iso-hyp",
      "iso-leg-from-hyp",
    ] as const);
    const k = randInt(rng, 1, 8);
    const matchHint: readonly [string, string] = [
      "Match the side the question names to the special-triangle ratio.",
      "התאימו את הצלע שהשאלה מציינת ליחס של המשולש המיוחד.",
    ];
    if (kind === "hyp-from-short") {
      const answer = String(2 * k);
      return {
        prompt: text(
          locale,
          `30-60-90 triangle. Shortest side = ${k}. Find the hypotenuse.`,
          `משולש 30-60-90. הניצב הקצר = ${k}. מצאו את היתר.`,
        ),
        expectedAnswer: answer,
        acceptedAnswers: [],
        hints: lines(locale, [
          ["The hypotenuse is twice the shortest side.", "היתר הוא פעמיים הניצב הקצר."],
          matchHint,
        ]),
        explanation: text(
          locale,
          `In a 30-60-90 triangle the hypotenuse is twice the side opposite 30°. Twice ${k} is ${answer}.`,
          `במשולש 30-60-90 היתר הוא פעמיים הצלע שמול 30°. פעמיים ${k} הם ${answer}.`,
        ),
      };
    }
    if (kind === "short-from-hyp") {
      return {
        prompt: text(
          locale,
          `30-60-90 triangle. Hypotenuse = ${2 * k}. Find the shortest side.`,
          `משולש 30-60-90. יתר = ${2 * k}. מצאו את הניצב הקצר.`,
        ),
        expectedAnswer: String(k),
        acceptedAnswers: [],
        hints: lines(locale, [
          ["The shortest side is half the hypotenuse.", "הניצב הקצר הוא חצי מהיתר."],
          matchHint,
        ]),
        explanation: text(
          locale,
          `The side opposite 30° is half the hypotenuse. Half of ${2 * k} is ${k}.`,
          `הצלע שמול 30° היא חצי מהיתר. חצי מ-${2 * k} הוא ${k}.`,
        ),
      };
    }
    if (kind === "long-from-short") {
      const radical = radicalAnswer(k, 3);
      return {
        prompt: text(
          locale,
          `30-60-90 triangle. Shortest side = ${k}. Find the longer leg.`,
          `משולש 30-60-90. הניצב הקצר = ${k}. מצאו את הניצב הארוך.`,
        ),
        expectedAnswer: radical.expectedAnswer,
        acceptedAnswers: radical.acceptedAnswers,
        hints: lines(locale, [
          ["The longer leg is the shortest side times the square root of three.", "הניצב הארוך הוא הניצב הקצר כפול שורש שלוש."],
          matchHint,
        ]),
        explanation: text(
          locale,
          `The side opposite 60° is the shortest side times sqrt(3). ${k} * sqrt(3) = ${radical.expectedAnswer}.`,
          `הצלע שמול 60° היא הניצב הקצר כפול sqrt(3). ${k} * sqrt(3) = ${radical.expectedAnswer}.`,
        ),
      };
    }
    if (kind === "short-from-long") {
      const radical = radicalAnswer(k, 3);
      return {
        prompt: text(
          locale,
          `30-60-90 triangle. Longer leg = ${radical.expectedAnswer}. Find the shortest side.`,
          `משולש 30-60-90. הניצב הארוך = ${radical.expectedAnswer}. מצאו את הניצב הקצר.`,
        ),
        expectedAnswer: String(k),
        acceptedAnswers: [],
        hints: lines(locale, [
          ["The shortest side is the longer leg divided by the square root of three.", "הניצב הקצר הוא הניצב הארוך חלקי שורש שלוש."],
          matchHint,
        ]),
        explanation: text(
          locale,
          `The longer leg is the short side times sqrt(3), so divide by sqrt(3). ${radical.expectedAnswer} / sqrt(3) = ${k}.`,
          `הניצב הארוך הוא הצלע הקצרה כפול sqrt(3), לכן מחלקים ב-sqrt(3). ${radical.expectedAnswer} / sqrt(3) = ${k}.`,
        ),
      };
    }
    if (kind === "long-from-hyp") {
      const radical = radicalAnswer(k, 3);
      return {
        prompt: text(
          locale,
          `30-60-90 triangle. Hypotenuse = ${2 * k}. Find the longer leg.`,
          `משולש 30-60-90. יתר = ${2 * k}. מצאו את הניצב הארוך.`,
        ),
        expectedAnswer: radical.expectedAnswer,
        acceptedAnswers: radical.acceptedAnswers,
        hints: lines(locale, [
          ["First find the short side as half the hypotenuse, then multiply by the square root of three.", "קודם מצאו את הצלע הקצרה כחצי מהיתר, ואז כפלו בשורש שלוש."],
          matchHint,
        ]),
        explanation: text(
          locale,
          `Half of ${2 * k} is ${k}, the short side. The longer leg is ${k} * sqrt(3) = ${radical.expectedAnswer}.`,
          `חצי מ-${2 * k} הוא ${k}, הצלע הקצרה. הניצב הארוך הוא ${k} * sqrt(3) = ${radical.expectedAnswer}.`,
        ),
      };
    }
    if (kind === "hyp-from-long") {
      const radical = radicalAnswer(k, 3);
      return {
        prompt: text(
          locale,
          `30-60-90 triangle. Longer leg = ${radical.expectedAnswer}. Find the hypotenuse.`,
          `משולש 30-60-90. הניצב הארוך = ${radical.expectedAnswer}. מצאו את היתר.`,
        ),
        expectedAnswer: String(2 * k),
        acceptedAnswers: [],
        hints: lines(locale, [
          ["Recover the short side by dividing by the square root of three, then double it.", "מצאו את הצלע הקצרה על ידי חלוקה בשורש שלוש, ואז הכפילו אותה."],
          matchHint,
        ]),
        explanation: text(
          locale,
          `${radical.expectedAnswer} / sqrt(3) = ${k}. The hypotenuse is twice the short side: ${2 * k}.`,
          `${radical.expectedAnswer} / sqrt(3) = ${k}. היתר הוא פעמיים הצלע הקצרה: ${2 * k}.`,
        ),
      };
    }
    if (kind === "iso-leg-from-hyp") {
      const radical = radicalAnswer(k, 2);
      return {
        prompt: text(
          locale,
          `45-45-90 triangle. Hypotenuse = ${radical.expectedAnswer}. Find a leg.`,
          `משולש 45-45-90. יתר = ${radical.expectedAnswer}. מצאו ניצב.`,
        ),
        expectedAnswer: String(k),
        acceptedAnswers: [],
        hints: lines(locale, [
          ["A leg is the hypotenuse divided by the square root of two.", "ניצב הוא היתר חלקי שורש שתיים."],
          matchHint,
        ]),
        explanation: text(
          locale,
          `The hypotenuse is a leg times sqrt(2), so divide by sqrt(2). ${radical.expectedAnswer} / sqrt(2) = ${k}.`,
          `היתר הוא ניצב כפול sqrt(2), לכן מחלקים ב-sqrt(2). ${radical.expectedAnswer} / sqrt(2) = ${k}.`,
        ),
      };
    }
    const radical = radicalAnswer(k, 2);
    return {
      prompt: text(
        locale,
        `45-45-90 triangle. Leg = ${k}. Find the hypotenuse.`,
        `משולש 45-45-90. ניצב = ${k}. מצאו את היתר.`,
      ),
      expectedAnswer: radical.expectedAnswer,
      acceptedAnswers: radical.acceptedAnswers,
      hints: lines(locale, [
        ["The hypotenuse is a leg times the square root of two.", "היתר הוא ניצב כפול שורש שתיים."],
        matchHint,
      ]),
      explanation: text(
        locale,
        `In a 45-45-90 triangle the hypotenuse is a leg times sqrt(2). ${k} * sqrt(2) = ${radical.expectedAnswer}.`,
        `במשולש 45-45-90 היתר הוא ניצב כפול sqrt(2). ${k} * sqrt(2) = ${radical.expectedAnswer}.`,
      ),
    };
  },

  "complex.multiply"(rng, locale) {
    let a = randInt(rng, -4, 4);
    let b = randInt(rng, -4, 4);
    let c = randInt(rng, -4, 4);
    let d = randInt(rng, -4, 4);
    if (b === 0 && d === 0) d = 2;
    if (a === 0 && b === 0) a = 1;
    if (c === 0 && d === 0) c = 1;
    const hintPairs: ReadonlyArray<readonly [string, string]> = [
      ["Multiply each part of the first factor by each part of the second.", "כפלו כל חלק של הגורם הראשון בכל חלק של הגורם השני."],
      ["Replace the square of the imaginary unit with negative one, then group like parts.", "החליפו את ריבוע היחידה המדומה בשלילי, ואז קבצו חלקים דומים."],
    ];
    let real = a * c - b * d;
    let imag = a * d + b * c;
    let expected = complexTerm(real, imag);
    for (
      let guard = 0;
      guard < 8 && hintPairs.some(([en, he]) => en.includes(expected) || he.includes(expected));
      guard += 1
    ) {
      a = a >= 3 ? a - 1 : a + 1;
      if (a === 0 && b === 0) a = 2;
      if (d === 0 && b === 0) d = 2;
      real = a * c - b * d;
      imag = a * d + b * c;
      expected = complexTerm(real, imag);
    }
    const accepted = new Set<string>();
    if (imag === 1 && real !== 0) accepted.add(`${real}+1i`);
    if (imag === -1 && real !== 0) accepted.add(`${real}-1i`);
    if (real === 0 && imag === 1) accepted.add("1i");
    if (real === 0 && imag === -1) accepted.add("-1i");
    accepted.delete(expected);
    return {
      prompt: text(
        locale,
        `Multiply. ${parenComplex(a, b)}${parenComplex(c, d)}`,
        `כפלו. ${parenComplex(a, b)}${parenComplex(c, d)}`,
      ),
      expectedAnswer: expected,
      acceptedAnswers: [...accepted],
      hints: lines(locale, hintPairs),
      explanation: text(
        locale,
        `Expand ${parenComplex(a, b)}${parenComplex(c, d)}. Real parts: ${a}*${c} - ${b}*${d} = ${real}. Imaginary parts: ${a}*${d} + ${b}*${c} = ${imag}. Because i^2 = -1, the product is ${expected}.`,
        `פותחים את ${parenComplex(a, b)}${parenComplex(c, d)}. חלק ממשי: ${a}*${c} - ${b}*${d} = ${real}. חלק מדומה: ${a}*${d} + ${b}*${c} = ${imag}. מכיוון ש-i^2 = -1, המכפלה היא ${expected}.`,
      ),
    };
  },

  "algebra.exponent-quotient"(rng, locale) {
    const n = randInt(rng, 2, 5);
    const exp = randInt(rng, 2, 4);
    const m = n + exp;
    const divisor = rng() < 0.5 ? choice(rng, [2, 3, 4]) : 1;
    const quotient = rng() < 0.5 ? randInt(rng, 2, 4) : 1;
    const coeff = divisor * quotient;
    const left = monomial(coeff, m);
    const right = monomial(divisor, n);
    const expected = monomial(quotient, exp);
    const accepted = new Set<string>([`x^(${exp})`.replace(/^/, quotient === 1 ? "" : String(quotient))]);
    if (quotient !== 1) accepted.add(`${quotient}*x^${exp}`);
    accepted.delete(expected);
    return {
      prompt: text(locale, `Simplify. ${left} / ${right}`, `פשטו. ${left} / ${right}`),
      expectedAnswer: expected,
      acceptedAnswers: [...accepted],
      hints: lines(locale, [
        ["When the bases match, subtract the bottom exponent from the top exponent.", "כשהבסיסים שווים, מחסרים את המעריך שבמכנה מהמעריך שבמונה."],
        ["A coefficient in front is divided separately from the exponents.", "מקדם מלפנים מתחלק בנפרד מן המעריכים."],
      ]),
      explanation: text(
        locale,
        `The bases match, so subtract the exponents: ${m} - ${n} = ${exp}.${quotient === divisor && divisor === 1 ? "" : ` Divide the coefficients: ${coeff} / ${divisor} = ${quotient}.`} The quotient is ${expected}.`,
        `הבסיסים שווים, לכן מחסרים את המעריכים: ${m} - ${n} = ${exp}.${quotient === divisor && divisor === 1 ? "" : ` מחלקים את המקדמים: ${coeff} / ${divisor} = ${quotient}.`} המנה היא ${expected}.`,
      ),
    };
  },

  "algebra.scientific-notation"(rng, locale) {
    const multiply = rng() < 0.5;
    if (multiply) {
      const renormalize = rng() < 0.55;
      const leftMantissa = renormalize ? randInt(rng, 4, 9) : randInt(rng, 1, 3);
      const rightMantissa = renormalize
        ? choice(
            rng,
            [2, 3, 4, 5, 6, 7, 8, 9].filter((n) => leftMantissa * n >= 10),
          )
        : randInt(rng, 1, 3);
      const leftExp = randInt(rng, 1, 5);
      const rightExp = randInt(rng, 1, 5);
      const rawMantissa = leftMantissa * rightMantissa;
      let mantissa = rawMantissa;
      let exp = leftExp + rightExp;
      while (mantissa >= 10) {
        mantissa /= 10;
        exp += 1;
      }
      const mantissaText = Number.isInteger(mantissa)
        ? String(mantissa)
        : String(Number(mantissa.toFixed(4)));
      const expected = `${mantissaText}×10^${exp}`;
      const renormalizeNote =
        rawMantissa >= 10
          ? ` The product ${rawMantissa} is at least 10, so rewrite it as ${mantissaText} × 10 and add one to the exponent.`
          : "";
      const renormalizeNoteHe =
        rawMantissa >= 10
          ? ` המכפלה ${rawMantissa} גדולה מ-10 או שווה לה, לכן כותבים אותה כ-${mantissaText} × 10 ומוסיפים אחד למעריך.`
          : "";
      return {
        prompt: text(
          locale,
          `Simplify. (${leftMantissa} × 10^${leftExp}) * (${rightMantissa} × 10^${rightExp})`,
          `פשטו. (${leftMantissa} × 10^${leftExp}) * (${rightMantissa} × 10^${rightExp})`,
        ),
        expectedAnswer: expected,
        acceptedAnswers: [
          `${mantissaText}*10^${exp}`,
          `${mantissaText}x10^${exp}`,
        ],
        hints: lines(locale, [
          ["Multiply the front numbers, then add the powers of ten.", "כופלים את המספרים מלפנים, ואז מחברים את חזקות העשר."],
          [
            "If the front number reaches ten or more, rewrite it so it is at least one and less than ten.",
            "אם המספר מלפנים מגיע לעשר או יותר, כתבו אותו מחדש כך שיהיה לפחות אחד וקטן מעשר.",
          ],
        ]),
        explanation: text(
          locale,
          `Multiply the front numbers: ${leftMantissa} * ${rightMantissa} = ${rawMantissa}. Add the exponents: ${leftExp} + ${rightExp} = ${leftExp + rightExp}.${renormalizeNote} The product is ${expected}.`,
          `כופלים את המספרים מלפנים: ${leftMantissa} * ${rightMantissa} = ${rawMantissa}. מחברים את המעריכים: ${leftExp} + ${rightExp} = ${leftExp + rightExp}.${renormalizeNoteHe} המכפלה היא ${expected}.`,
        ),
      };
    }
    const divisor = randInt(rng, 1, 3);
    const quotient = randInt(rng, 1, Math.floor(9 / divisor));
    const mantissa = divisor * quotient;
    const rightExp = randInt(rng, 1, 4);
    const exp = randInt(rng, 1, 4);
    const leftExp = rightExp + exp;
    const expected = `${quotient}×10^${exp}`;
    return {
      prompt: text(
        locale,
        `Simplify. (${mantissa} × 10^${leftExp}) / (${divisor} × 10^${rightExp})`,
        `פשטו. (${mantissa} × 10^${leftExp}) / (${divisor} × 10^${rightExp})`,
      ),
      expectedAnswer: expected,
      acceptedAnswers: [`${quotient}*10^${exp}`, `${quotient}x10^${exp}`],
      hints: lines(locale, [
        ["Divide the front numbers, then subtract the powers of ten.", "מחלקים את המספרים מלפנים, ואז מחסרים את חזקות העשר."],
        ["Write the result as one number times a power of ten.", "כותבים את התוצאה כמספר אחד כפול חזקה של עשר."],
      ]),
      explanation: text(
        locale,
        `Divide the front numbers: ${mantissa} / ${divisor} = ${quotient}. Subtract the exponents: ${leftExp} - ${rightExp} = ${exp}. The quotient is ${expected}.`,
        `מחלקים את המספרים מלפנים: ${mantissa} / ${divisor} = ${quotient}. מחסרים את המעריכים: ${leftExp} - ${rightExp} = ${exp}. המנה היא ${expected}.`,
      ),
    };
  },

  "algebra.absolute-value"(rng, locale) {
    const mid = randInt(rng, -3, 3);
    const halfGap = randInt(rng, 1, 4);
    const low = mid - halfGap;
    const high = mid + halfGap;
    const a = randInt(rng, 1, 4);
    const b = -a * mid;
    const c = a * halfGap;
    const expr = compactLinear(a, b);
    const expected = `x=${low}, x=${high}`;
    return {
      prompt: text(locale, `Solve. |${expr}| = ${c}`, `פתרו. |${expr}| = ${c}`),
      expectedAnswer: expected,
      acceptedAnswers: [`x=${high}, x=${low}`],
      hints: lines(locale, [
        ["Split the equation into the positive case and the negative case.", "פצלו את המשוואה למקרה חיובי ולמקרה שלילי."],
        ["Both results are solutions when the number on the right is positive.", "שתי התוצאות הן פתרונות כשהמספר באגף הימני חיובי."],
      ]),
      explanation: text(
        locale,
        `|${expr}| = ${c} splits into ${expr} = ${c} and ${expr} = -${c}. The first gives x=${high}. The second gives x=${low}. The solutions are ${expected}.`,
        `|${expr}| = ${c} מתפצלת ל-${expr} = ${c} ול-${expr} = -${c}. הראשונה נותנת x=${high}. השנייה נותנת x=${low}. הפתרונות הם ${expected}.`,
      ),
    };
  },

  "algebra.evaluate-linear"(rng, locale) {
    const t = randInt(rng, -3, 5);
    let a = randInt(rng, -4, 5);
    if (a === 0) a = 2;
    const b = randInt(rng, -6, 6);
    const expr = formatLinearFn(a, b);
    const value = a * t + b;
    return {
      prompt: text(
        locale,
        `Find f(${t}) for f(x) = ${expr}.`,
        `מצאו את f(${t}) עבור f(x) = ${expr}.`,
      ),
      expectedAnswer: String(value),
      acceptedAnswers: [`f(${t})=${value}`, `f(${t}) = ${value}`],
      hints: lines(locale, [
        ["Replace the variable with the given input.", "החליפו את המשתנה בקלט הנתון."],
        ["Multiply first, then add the constant.", "קודם כופלים, ואז מחברים את הקבוע."],
      ]),
      explanation: text(
        locale,
        `f(x) = ${expr}. Substitute ${t} for x: ${a}*(${t}) + ${b} = ${value}.`,
        `f(x) = ${expr}. מציבים ${t} במקום x: ${a}*(${t}) + ${b} = ${value}.`,
      ),
    };
  },

  "algebra.slope-intercept-line"(rng, locale) {
    const m = choice(rng, [-4, -3, -2, -1, 1, 2, 3, 4]);
    const x1 = randInt(rng, -4, 4);
    const y1 = randInt(rng, -6, 6);
    const b = y1 - m * x1;
    const expected = slopeInterceptEquation(m, b);
    const product = m * x1;
    return {
      prompt: text(
        locale,
        `Slope m = ${m}, through (${x1}, ${y1}). Write y = mx + b.`,
        `שיפוע m = ${m}, דרך (${x1}, ${y1}). כתבו y = mx + b.`,
      ),
      expectedAnswer: expected,
      acceptedAnswers: [],
      hints: lines(locale, [
        ["Slope-intercept form is y = mx + b.", "צורת שיפוע-חותך היא y = mx + b."],
        ["Substitute the known point and solve for the intercept.", "הציבו את הנקודה הידועה ומצאו את החותך."],
      ]),
      explanation: text(
        locale,
        `The slope is ${m}. Substitute (${x1}, ${y1}): ${y1} = (${m})*(${x1}) + b, so b = ${y1} - (${m})*(${x1}) = ${y1} - (${product}) = ${b}. The line is ${expected}.`,
        `השיפוע הוא ${m}. מציבים את (${x1}, ${y1}): ${y1} = (${m})*(${x1}) + b, לכן b = ${y1} - (${m})*(${x1}) = ${y1} - (${product}) = ${b}. הישר הוא ${expected}.`,
      ),
    };
  },

  "algebra.quadratic-formula"(rng, locale) {
    const halfRoots = rng() < 0.45;
    let lead = choice(rng, [1, 2, 3]);
    let low: number;
    let high: number;
    let linear: number;
    let constant: number;
    if (halfRoots) {
      // Roots p/2 and q/2 with odd p,q so the roots are non-integers.
      let p = choice(rng, [-9, -7, -5, -3, -1, 1, 3, 5, 7]);
      let q = choice(rng, [-9, -7, -5, -3, -1, 1, 3, 5, 7].filter((n) => n !== p));
      if (p > q) {
        const swap = p;
        p = q;
        q = swap;
      }
      lead = choice(rng, [1, 2]);
      // lead*(2x-p)*(2x-q) = lead*(4x^2 - 2(p+q)x + pq)
      linear = -lead * 2 * (p + q);
      constant = lead * p * q;
      lead = lead * 4;
      low = p / 2;
      high = q / 2;
    } else {
      low = randInt(rng, -5, 3);
      high = randInt(rng, low + 1, 5);
      if (low === high) high = low + 1;
      linear = -lead * (low + high);
      constant = lead * low * high;
    }
    const expanded = poly([lead, linear, constant]);
    const disc = linear * linear - 4 * lead * constant;
    const discRoot = Math.round(Math.sqrt(disc));
    const lowText = formatRoot(low);
    const highText = formatRoot(high);
    const expected = `x=${lowText}, x=${highText}`;
    return {
      prompt: text(
        locale,
        `Solve with the quadratic formula. ${expanded} = 0`,
        `פתרו בעזרת נוסחת השורשים. ${expanded} = 0`,
      ),
      expectedAnswer: expected,
      acceptedAnswers: [`x=${highText}, x=${lowText}`],
      hints: lines(locale, [
        ["The quadratic formula uses the discriminant under a square root.", "נוסחת השורשים משתמשת בדלתא שמתחת לשורש."],
        ["Divide the sum and the difference by twice the leading coefficient.", "מחלקים את הסכום ואת ההפרש בפעמיים המקדם המוביל."],
      ]),
      explanation: text(
        locale,
        `Here a = ${lead}, b = ${linear}, and c = ${constant}. The discriminant is ${linear}^2 - 4*${lead}*${constant} = ${disc}, and its square root is ${discRoot}. The roots are (${-linear} ± ${discRoot}) / ${2 * lead}, which are x=${highText} and x=${lowText}. In order, the solutions are ${expected}.`,
        `כאן a = ${lead}, b = ${linear}, ו-c = ${constant}. הדלתא היא ${linear}^2 - 4*${lead}*${constant} = ${disc}, והשורש שלה הוא ${discRoot}. השורשים הם (${-linear} ± ${discRoot}) / ${2 * lead}, כלומר x=${highText} ו-x=${lowText}. לפי הסדר, הפתרונות הם ${expected}.`,
      ),
    };
  },

  "algebra.rational-simplify"(rng, locale) {
    let shared = randInt(rng, -5, 5);
    let other = randInt(rng, -5, 5);
    if (shared === 0) shared = 2;
    if (other === 0 || other === shared) other = shared > 0 ? -2 : 2;
    if (other === 0 || other === shared) other = 3;
    const numerator = `${parenFactor(shared)}${parenFactor(other)}`;
    const denominator = parenFactor(shared);
    const simplified = other > 0 ? `x+${other}` : `x-${Math.abs(other)}`;
    const excluded = formatExcluded(shared);
    const expected = `${simplified}, ${excluded}`;
    return {
      prompt: text(
        locale,
        `Simplify. ${numerator}/${denominator}`,
        `פשטו. ${numerator}/${denominator}`,
      ),
      expectedAnswer: expected,
      acceptedAnswers: [
        simplified,
        parenFactor(other),
        `${parenFactor(other)}, ${excluded}`,
        `${simplified} (${excluded})`,
      ],
      hints: lines(locale, [
        ["Cancel the factor shared by the numerator and the denominator.", "צמצמו את הגורם המשותף למונה ולמכנה."],
        ["State the simplified expression and the value that makes the original denominator zero.", "כתבו את הביטוי המצומצם ואת הערך שמאפס את המכנה המקורי."],
      ]),
      explanation: text(
        locale,
        `${denominator} appears in both ${numerator} and the denominator, so it cancels. The factor that remains is ${simplified}. The original denominator is zero when ${excludedZeroAt(shared)}, so exclude that value: ${expected}.`,
        `${denominator} מופיע גם ב-${numerator} וגם במכנה, לכן הוא מצטמצם. הגורם שנשאר הוא ${simplified}. המכנה המקורי מתאפס כאשר ${excludedZeroAt(shared)}, ולכן מוציאים את הערך הזה: ${expected}.`,
      ),
    };
  },

  "algebra.rational-exponent"(rng, locale) {
    const kind = choice(rng, ["half", "three-halves", "two-thirds"] as const);
    const root = kind === "half" ? randInt(rng, 2, 12) : randInt(rng, 2, 5);
    const den = kind === "two-thirds" ? 3 : 2;
    const num = kind === "half" ? 1 : kind === "three-halves" ? 3 : 2;
    const base = repeatMultiply(1, root, den);
    const value = repeatMultiply(1, root, num);
    const expected = String(value);
    return {
      prompt: text(
        locale,
        `Simplify. ${base}^(${num}/${den})`,
        `פשטו. ${base}^(${num}/${den})`,
      ),
      expectedAnswer: expected,
      acceptedAnswers: [],
      hints: lines(locale, [
        ["A fractional exponent is a root followed by a power.", "מעריך שבור הוא שורש ואחריו חזקה."],
        ["Take the root named by the denominator, then raise to the numerator.", "הוציאו את השורש שהמכנה מציין, ואז העלו במעריך שבמונה."],
      ]),
      explanation: text(
        locale,
        `${base}^(${num}/${den}) means the root of index ${den}, then the power ${num}. The root of ${base} is ${root}, and raising it to ${num} gives ${expected}.`,
        `${base}^(${num}/${den}) פירושו שורש מסדר ${den}, ואז חזקה ${num}. השורש של ${base} הוא ${root}, והעלאה בחזקה ${num} נותנת ${expected}.`,
      ),
    };
  },

  "algebra.exponential-value"(rng, locale) {
    const base = randInt(rng, 2, 5);
    const exp = randInt(rng, 2, 4);
    const coeff = rng() < 0.5 ? 1 : randInt(rng, 2, 4);
    const power = repeatMultiply(1, base, exp);
    const value = coeff * power;
    const stem = coeff === 1 ? `${base}^${exp}` : `${coeff}*${base}^${exp}`;
    return {
      prompt: text(locale, `Evaluate. ${stem}`, `חשבו. ${stem}`),
      expectedAnswer: String(value),
      acceptedAnswers: [],
      hints: lines(locale, [
        ["Evaluate the power before multiplying by the number in front.", "חשבו את החזקה לפני הכפל במספר מלפנים."],
        ["Finish with a single number; do not leave a product unsimplified.", "סיימו במספר אחד; אל תשאירו מכפלה לא מפושטת."],
      ]),
      explanation: text(
        locale,
        coeff === 1
          ? `${base}^${exp} is ${base} used as a factor ${exp} times, which equals ${value}.`
          : `${base}^${exp} = ${power}. Multiply by the front number: ${coeff} * ${power} = ${value}.`,
        coeff === 1
          ? `${base}^${exp} הוא ${base} כגורם ${exp} פעמים, והתוצאה היא ${value}.`
          : `${base}^${exp} = ${power}. כופלים במספר מלפנים: ${coeff} * ${power} = ${value}.`,
      ),
    };
  },
};
